<script>
    import Vue from 'vue';
    import DotSpinner from '@/core/components/common/dot-spinner';
    import Price from '@/core/components/price';

    import noPhoto from '@/core/assets/images/no-photo.svg';
    import iconStarFull from '@/core/assets/images/icons/offer-star-full.svg';
    import iconStarHalf from '@/core/assets/images/icons/offer-star-half.svg';
    import iconStarZero from '@/core/assets/images/icons/offer-star-zero.svg';
    import cartIcon from '@/core/assets/images/icons/add-to-cart.svg';
    import arrowDownIcon from '@/core/assets/images/icons/arrow-down.svg';
    import popupSlider from './helper/popup-slider';
    import cutText from '@/core/helpers/cut-text';
    import delegateEvent from '@/core/helpers/delegate-event';
    import BaseTemplateString from 'raw-loader!./templates/base.html';
    import RowTemplateString from 'raw-loader!./templates/row-template.html';
    import Cart from '@/core/components/cart';

    const DEFAULT_TEMPLATE = '$base';
    const templatesCache = {};

    export default {
        components: {
            DotSpinner,
            Cart,
            Price,
        },
        props: {
            offer: {
                type: Object,
            },
            templates: {
                type: Object,
                required: false,
            },
            isCartAvailable: {
                type: Boolean,
                required: true,
            },
        },
        data() {
            return {
                isActiveImageLoaded: false,
                activeImageIndex: 0,
                showModal: false,
                errorImgLoad: false,
                errorImgLoadRow: false,
                errorCustomImg: false,
                isShowNameTooltip: false,
            };
        },
        computed: {
            showCartButton() {
                if (this.labels !== undefined) {
                    if (this.labels.some(label => label.id === 'notAvailable')) {
                        return false;
                    }
                }
                let prices = false;
                if (this.offer.prices) {
                    prices = !!Object.keys(this.offer.prices).length && this.$configuration.offer?.prices.length;
                }
                
                return (this.offer.price || prices) && this.isCartAvailable && this.offer.available;
            },
            name() {
                return cutText((this.offer.name || `${this.offer.model} ${this.offer.vendor}`), this.$configuration.features.limitNameChars);
            },
            starIconsByType() {
                return {
                    full: iconStarFull,
                    half: iconStarHalf,
                    zero: iconStarZero,
                };
            },

            noPhotoURL() {
                return this.errorCustomImg || noPhoto;
            },
            cartIcon() {
                return cartIcon;
            },
            hasImages() {
                return this.offer.pictures && this.offer?.pictures?.length;
            },
            hasImagesRow() {
                return this.offer.pictures && this.offer?.pictures?.length && !this.errorImgLoadRow;
            },
            hasLoadedImage() {
                return this.hasImages && this.isActiveImageLoaded && !this.errorImgLoad;
            },
            hasLoadedImageRow() {
                return this.hasImagesRow && this.isActiveImageLoaded && !this.errorImgLoad;
            },
            hasMultipleImages() {
                return this.offer?.pictures?.length > 1;
            },
            arrowDownIcon() {
                return arrowDownIcon;
            },
            labels() {
                return this.offer.labels || [];
            },
            snippet() {
                return this.offer.snippet || null;
            },
            reviewCount() {
                return this.offer.reviewCount;
            },
            rating() {
                // Example: 1.3 => 1.5, 1.1 => 1, 4.9 => 5, 2.4 => 2.5
                return Math.round(this.offer.rating * 2) / 2;
            },
            starItems() {
                if (!this.rating) {
                    return [];
                }
                const maxRating = 5;
                let items = [];
                let ratingCounter = this.rating;
                for (let rating = maxRating; rating > 0; rating--) {
                    items.push(ratingCounter >= 1 ? 'full' : ratingCounter > 0 ? 'half' : 'zero');
                    ratingCounter--;
                }
                return items;
            },
        },
        created() {
            if (this.$configuration.errorImg) {
                const errorImg = document.createElement('img');
                errorImg.src = this.$configuration.errorImg;
                errorImg.onload = () => {
                    this.errorCustomImg = this.$configuration.errorImg;
                };
                errorImg.onerror = () => {
                    this.errorCustomImg = false;
                };
            }
        },
        mounted() {
            const NODE_COMMENT_CODE = 8;
            if (this.$el && this.$el.nodeType !== NODE_COMMENT_CODE) {
                this.$el.setAttribute(
                    'data-template-id',
                    this.offer.templateId || DEFAULT_TEMPLATE,
                );
            }

            this.setNameTooltipVisibility();
        },
        methods: {
            onclick() {
                const hash = window.location.hash;
                const params = new URLSearchParams(hash.substring(1));
                params.set('idOffer', this.offer.id);
                window.location.hash = decodeURIComponent(params.toString());
            },
            setNameTooltipVisibility() {
                const offerNameText = this.$refs.offerName.innerHTML;
                const numberOfDots = 3;

                if (offerNameText.length >= numberOfDots) {
                    if (offerNameText.slice(-numberOfDots) === '...') {
                        this.isShowNameTooltip = true;
                    }
                }
            },
            errorImg(event) {
                if (this.errorCustomImg) {
                    event.target.src = this.errorCustomImg;
                    this.errorImgLoadRow = true;
                } else {
                    event.target.src = require('@/core/assets/images/no-photo.png');
                    this.errorImgLoadRow = true;
                }
            },
            stubLinkClick() {
                return false;
            },
            showThumbnails() {
                popupSlider(this.offer.pictures);
            },
            handleClick(event) {
                delegateEvent(event, {
                    addToCart: () => this.addToCart(this.offer),
                    plusCart: () => this.plusCart(this.offer),
                    minusCart: () => this.minusCart(this.offer),
                    nextImage: () => this.handleNextImage(event),
                });
            },
            setRealImgSrc(imgEl) {
                imgEl.src = imgEl.getAttribute('data-src');
            },
            handleNextImage(event) {
                event.preventDefault();
                let index = this.activeImageIndex + 1;
                if (index >= this.offer.pictures.length) {
                    index = 0;
                }
                this.activeImageIndex = index;
                this.setRealImgSrc(this.$refs[`thumbnail-${index}`][0]);
            },
            handleImageLoad(imgIndex) {
                if (imgIndex === this.activeImageIndex || 0) {
                    this.$nextTick(() => {
                        this.isActiveImageLoaded = true;
                    });
                }
            },
        },
        render(createElement) {
            const render = (context, templateId) => {
                const templates = context.templates || {};
                const templateOffer = this.offer.template;
                let templateFunc = templatesCache[templateId];
                let template = (this.$configuration.layout.templateOffer === 'rows') ? RowTemplateString : BaseTemplateString;
                if (!templateFunc) {
                    try {
                        templatesCache[templateId] = Vue.compile(
                            templates[templateId] || templateOffer || template,
                        );
                        templateFunc = templatesCache[templateId];
                    } catch (error) {
                        console.error(`Failed to compile template "${templateId}"`, error);
                        templateFunc = Vue.compile(BaseTemplateString);
                    }
                }
                return templateFunc.render.call(context, createElement);
            };
            const templateId = this.offer.template || DEFAULT_TEMPLATE;
            try {
                return render(this, templateId);
            } catch (error) {
                try {
                    return render(this, DEFAULT_TEMPLATE);
                } catch (error) {
                    console.error('Failed to render template', error);
                }
            }
            return createElement('span', {}, [
                `Failed to render template ${templateId}`,
            ]);
        },
    };
</script>